import { useEffect } from 'react';
import { ArrowRight, Check, Activity, Target, Network, Zap, ExternalLink } from 'lucide-react';
import { profiles, jobOpenings, generalInterestConfig } from '@/content/team';

function Crosshair({ className = '' }: { className?: string }) {
  return (
    <div className={`absolute w-3 h-3 flex items-center justify-center opacity-30 ${className}`}>
      <div className="absolute w-full h-[1px] bg-foreground"></div>
      <div className="absolute h-full w-[1px] bg-foreground"></div>
    </div>
  );
}

export default function Team() {
  // Handle hash navigation manually to ensure smooth scrolling and focus management for accessibility
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Team & Careers | Plasmics';
    const values: [string, string][] = [
      ['meta[name="description"]', 'Explore Plasmics engineering focus areas, development-team updates and approved career opportunities.'],
      ['meta[property="og:title"]', 'Team & Careers | Plasmics'],
      ['meta[property="og:description"]', 'Engineering at Plasmics: team updates and career opportunities.'],
    ];
    const previous = values.map(([selector, content]) => {
      const element = document.querySelector<HTMLMetaElement>(selector);
      const old = element?.content;
      if (element) element.content = content;
      return { element, old };
    });
    return () => {
      document.title = previousTitle;
      previous.forEach(({ element, old }) => { if (element && old !== undefined) element.content = old; });
    };
  }, []);

  return (
    <main className="w-full bg-background overflow-hidden text-foreground pt-24">
      {/* HEADER SECTION */}
      <section className="relative py-16 md:py-24 px-6 md:px-12 border-b border-border bg-white">
        <div className="container mx-auto z-10 relative">
          <div className="max-w-4xl mx-auto flex flex-col gap-6 animate-in-fade">
            <div className="flex items-center gap-4 mb-4">
              <span className="w-8 h-[1px] bg-primary"></span>
              <span className="font-mono text-primary text-xs font-bold tracking-[0.3em] uppercase">
                System Development
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-sans font-light tracking-tight text-foreground leading-[1.05] text-balance">
              Engineering the <br/>
              <span className="font-bold">production stack.</span>
            </h1>
            
            <div className="w-12 h-1 bg-secondary my-4"></div>
            
            <p className="text-muted-foreground text-lg md:text-xl font-light leading-relaxed">
              Plasmics brings hardware and intelligent software together in additive manufacturing. Explore the engineering focus behind the platform and find updates about our development team and career opportunities.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
              <div className="border border-border p-6 bg-muted/20">
                <h3 className="font-mono text-xs font-bold tracking-widest uppercase mb-4 text-foreground flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" /> Focus Areas
                </h3>
                <ul className="space-y-3">
                  {[
                    'Active heated chambers', 
                    'Multi-material printing capabilities', 
                    'Computer vision and ML integration', 
                    'Decentralized manufacturing frameworks'
                  ].map((area, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground font-light">
                      <Check className="w-3.5 h-3.5 mt-0.5 text-secondary shrink-0" />
                      <span>{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-border p-6 bg-foreground text-background flex flex-col justify-between">
                <div>
                  <h3 className="font-mono text-xs font-bold tracking-widest uppercase mb-4 text-white flex items-center gap-2">
                    <Network className="w-4 h-4 text-secondary" /> Mission Parameters
                  </h3>
                  <p className="text-sm text-background/80 font-light leading-relaxed mb-6">
                    Our vision is decentralized manufacturing: enabling people to produce parts where they are needed. Thermal control, materials and software are central to that engineering direction.
                  </p>
                </div>
                <a href="#open-positions" className="px-6 py-4 bg-primary text-foreground font-mono text-xs font-bold tracking-widest uppercase hover:bg-white hover:text-foreground transition-colors flex items-center justify-between w-full group outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                  Join the team now
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROFILES SECTION */}
      <section className="py-24 relative bg-muted/5 border-b border-border scroll-mt-24 focus-visible:outline-none" id="profiles">
        <div className="container mx-auto px-6 md:px-12">
          <div className="mb-16">
            <span className="font-mono text-secondary text-xs font-bold tracking-[0.3em] uppercase block mb-4">
              People behind Plasmics
            </span>
            <h2 className="text-3xl md:text-4xl font-light text-balance leading-tight">
              Development <span className="font-bold">Team.</span>
            </h2>
          </div>

          {profiles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {profiles.filter(profile => profile.approved).map((profile) => (
                <div key={profile.id} className="group bg-white border border-border relative overflow-hidden flex flex-col h-full">
                  <div className="absolute top-0 left-0 w-full h-1 bg-secondary transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 z-10" />
                  <div className="relative aspect-[4/5] bg-muted overflow-hidden border-b border-border">
                    <img 
                      src={profile.portrait} 
                      alt={profile.alt} 
                      className="w-full h-full object-cover grayscale opacity-90 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 mix-blend-multiply" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col bg-white">
                    <h3 className="text-xl font-bold mb-1">{profile.name}</h3>
                    <div className="font-mono text-[10px] text-secondary tracking-widest uppercase mb-4">{profile.role}</div>
                    <p className="text-muted-foreground font-light text-sm leading-relaxed mt-auto">
                      {profile.bio}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full border border-border bg-white p-12 md:p-24 flex flex-col items-center justify-center text-center relative group">
              <Crosshair className="top-4 left-4" />
              <Crosshair className="top-4 right-4" />
              <Crosshair className="bottom-4 left-4" />
              <Crosshair className="bottom-4 right-4" />
              
              <div className="w-16 h-16 bg-muted mb-8 flex items-center justify-center border border-border group-hover:border-secondary transition-colors">
                <Target className="w-6 h-6 text-muted-foreground opacity-50 group-hover:text-secondary group-hover:opacity-100 transition-colors" />
              </div>
              <h3 className="text-2xl font-light mb-3 text-foreground">Profiles being prepared.</h3>
              <p className="text-muted-foreground max-w-md font-light text-sm leading-relaxed">
                We’re preparing an introduction to the development team. Names, roles and portraits will appear here once their publication is approved.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* OPEN POSITIONS SECTION */}
      <section tabIndex={-1} className="py-24 relative bg-white border-b border-border scroll-mt-24 focus-visible:outline-none" id="open-positions">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8 mb-16">
            <div className="max-w-2xl">
              <span className="font-mono text-primary text-xs font-bold tracking-[0.3em] uppercase block mb-4 flex items-center gap-3">
                <Zap className="w-4 h-4" /> System Expansion
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-balance leading-tight">
                Open <span className="font-bold">positions.</span>
              </h2>
            </div>
            <p className="text-muted-foreground max-w-sm font-light text-sm md:text-base border-l-2 border-primary pl-4">
              Approved role details and application destinations will be published here.
            </p>
          </div>

          {jobOpenings.length > 0 ? (
            <div className="flex flex-col gap-6">
              {jobOpenings.filter(job => job.approved).map((job) => (
                <div key={job.id} className="border border-border bg-white hover:border-foreground transition-colors group flex flex-col lg:flex-row relative">
                  <div className="absolute left-0 top-0 w-1 h-full bg-primary transform scale-y-0 group-hover:scale-y-100 transition-transform origin-bottom duration-300" />
                  
                  <div className="flex-1 p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-border">
                    <div className="flex flex-wrap gap-3 mb-6">
                      <span className="px-3 py-1 bg-muted/50 border border-border font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{job.employmentType}</span>
                      <span className="px-3 py-1 bg-muted/50 border border-border font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{job.location}</span>
                      <span className="px-3 py-1 bg-muted/50 border border-border font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{job.workingArrangement}</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-4">{job.title}</h3>
                    <p className="text-muted-foreground font-light text-sm leading-relaxed mb-8 max-w-3xl">
                      {job.summary}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-mono text-[10px] text-foreground tracking-widest uppercase mb-4 border-b border-border pb-2">Responsibilities</h4>
                        <ul className="space-y-2">
                          {job.responsibilities.map((req, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground font-light">
                              <span className="w-1 h-1 bg-secondary rounded-full mt-2 shrink-0" />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-mono text-[10px] text-foreground tracking-widest uppercase mb-4 border-b border-border pb-2">Requirements</h4>
                        <ul className="space-y-2">
                          {job.requirements.map((req, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground font-light">
                              <span className="w-1 h-1 bg-primary rounded-full mt-2 shrink-0" />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="w-full lg:w-72 bg-muted/10 p-8 flex flex-col justify-center items-center text-center">
                    <a 
                      href={job.applicationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Apply for ${job.title} (opens a new tab)`}
                      className="px-6 py-4 w-full bg-foreground text-background font-mono text-xs font-bold tracking-widest uppercase hover:bg-primary transition-colors flex items-center justify-between group/btn border border-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                      Apply Now
                      <ExternalLink className="w-4 h-4 group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5 transition-transform" />
                    </a>
                    <span className="font-mono text-[9px] text-muted-foreground uppercase tracking-widest mt-4">
                      Approved application destination
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full border border-border bg-white p-12 flex flex-col md:flex-row gap-8 items-center justify-between relative group">
              <div className="absolute top-0 left-0 w-1 h-full bg-secondary" />
              <div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Openings not yet published.</h3>
                <p className="text-muted-foreground font-light text-sm max-w-xl leading-relaxed">
                  No verified openings are published here yet. This does not mean Plasmics is not hiring. Please check back for approved role details and application links.
                </p>
              </div>
              <div className="w-16 h-16 border border-border flex items-center justify-center shrink-0 bg-muted/30 group-hover:border-secondary transition-colors">
                <Activity className="w-6 h-6 text-muted-foreground opacity-50 group-hover:text-secondary group-hover:opacity-100 transition-colors" />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* GENERAL INTEREST SECTION */}
      <section className="py-24 relative bg-foreground text-background scroll-mt-24 focus-visible:outline-none" id="contact">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-light mb-6">Interested in Plasmics?</h2>
            <p className="text-background/70 font-light text-sm leading-relaxed mb-8">
              {generalInterestConfig.description}
            </p>
            <div className="inline-flex max-w-full flex-col sm:flex-row items-center gap-4 px-4 py-6 border border-background/20 bg-background/5">
              <span className="font-mono text-xs font-bold tracking-widest uppercase text-secondary">{generalInterestConfig.label}:</span>
              <a 
                href={generalInterestConfig.url}
                className="font-mono text-sm hover:text-primary transition-colors flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
              >
                {generalInterestConfig.linkText}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
